import { useEffect, useMemo, useState } from "react";
import {
  getExchangeRateAutoSyncStatus,
  getExchangeRateMatrix,
  getExchangeRateOverview,
  getExchangeRatePairs,
  getExchangeRateSyncStatus,
  syncExchangeRates,
  syncExchangeRatesStream,
} from "../../services/exchangeRateService";
import { useCurrencyCatalog } from "../../contexts/CurrencyCatalogContext";
import {
  createDefaultFilters,
  formatSort,
  nextSort,
  normalizeErrorMessage,
} from "./exchangeRatePageUtils";
import { createExchangeRateSyncHandlers } from "./exchangeRateSyncHandlers";

export default function useExchangeRatesPage({ user, showToast, t }) {
  const { currencies: supportedCurrencies } = useCurrencyCatalog();
  const [activeTab, setActiveTab] = useState("overview");

  const [overview, setOverview] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [pairs, setPairs] = useState(null);
  const [syncStatus, setSyncStatus] = useState([]);
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);

  const [overviewLoading, setOverviewLoading] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);

  const [overviewError, setOverviewError] = useState("");
  const [matrixError, setMatrixError] = useState("");
  const [pairsError, setPairsError] = useState("");
  const [syncError, setSyncError] = useState("");
  const [autoSyncError, setAutoSyncError] = useState("");

  const [syncing, setSyncing] = useState(false);
  const [fullSyncProgress, setFullSyncProgress] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState("EUR");
  const [selectedCell, setSelectedCell] = useState(null);
  const [filters, setFilters] = useState(createDefaultFilters());
  const [matrixPage, setMatrixPage] = useState(1);
  const [matrixPageSize, setMatrixPageSize] = useState(12);
  const [matrixSort, setMatrixSort] = useState({
    key: "baseCurrency",
    direction: "asc",
  });
  const [pairsPage, setPairsPage] = useState(1);
  const [pairsPageSize, setPairsPageSize] = useState(50);
  const [pairsSort, setPairsSort] = useState({
    key: "rateDate",
    direction: "desc",
  });

  const sortedSyncStatus = useMemo(
    () =>
      [...syncStatus].sort((left, right) => {
        const leftTime = left?.lastAttemptAt
          ? new Date(left.lastAttemptAt).getTime()
          : 0;
        const rightTime = right?.lastAttemptAt
          ? new Date(right.lastAttemptAt).getTime()
          : 0;
        return rightTime - leftTime;
      }),
    [syncStatus],
  );

  const availableBaseCurrencies = useMemo(
    () => supportedCurrencies.filter(Boolean),
    [supportedCurrencies],
  );
  const availableQuoteCurrencies = useMemo(
    () => supportedCurrencies.filter(Boolean),
    [supportedCurrencies],
  );

  const buildMatrixParams = (overrides = {}) => {
    const nextFilters = overrides.filters || filters;
    return {
      baseCurrencies: nextFilters.baseCurrency
        ? [nextFilters.baseCurrency]
        : undefined,
      quoteCurrencies: nextFilters.quoteCurrency
        ? [nextFilters.quoteCurrency]
        : undefined,
      source: nextFilters.source || undefined,
      status: nextFilters.status || undefined,
      rateDate: nextFilters.rateDate || undefined,
      includeDerived: nextFilters.includeDerived,
      usedOnly: nextFilters.usedOnly,
      search: nextFilters.search || undefined,
      page: overrides.page || matrixPage,
      pageSize: overrides.pageSize || matrixPageSize,
      sort: formatSort(overrides.sort || matrixSort),
    };
  };

  const buildPairsParams = (overrides = {}) => {
    const nextFilters = overrides.filters || filters;
    return {
      baseCurrency: nextFilters.baseCurrency || undefined,
      quoteCurrency: nextFilters.quoteCurrency || undefined,
      source: nextFilters.source || undefined,
      status: nextFilters.status || undefined,
      rateDate: nextFilters.rateDate || undefined,
      includeDerived: nextFilters.includeDerived,
      usedOnly: nextFilters.usedOnly,
      search: nextFilters.search || undefined,
      sort: formatSort(overrides.sort || pairsSort),
      page: overrides.page || pairsPage,
      pageSize: overrides.pageSize || pairsPageSize,
    };
  };

  const loadOverview = async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const data = await getExchangeRateOverview({
        defaultBaseCurrency: baseCurrency,
      });
      setOverview(data);
    } catch (err) {
      setOverviewError(
        normalizeErrorMessage(err, "Failed to load exchange-rate overview."),
      );
      if (!overview) setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadMatrix = async (overrides = {}) => {
    setMatrixLoading(true);
    setMatrixError("");
    try {
      const data = await getExchangeRateMatrix(buildMatrixParams(overrides));
      setMatrix(data);
    } catch (err) {
      setMatrixError(
        normalizeErrorMessage(err, "Failed to load exchange-rate matrix."),
      );
    } finally {
      setMatrixLoading(false);
    }
  };

  const loadPairs = async (overrides = {}) => {
    setPairsLoading(true);
    setPairsError("");
    try {
      const data = await getExchangeRatePairs(buildPairsParams(overrides));
      setPairs(data);
    } catch (err) {
      setPairsError(
        normalizeErrorMessage(err, "Failed to load exchange-rate pairs."),
      );
    } finally {
      setPairsLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    setSyncLoading(true);
    setSyncError("");
    try {
      const data = await getExchangeRateSyncStatus();
      setSyncStatus(Array.isArray(data) ? data : []);
    } catch (err) {
      setSyncError(
        normalizeErrorMessage(
          err,
          t(
            "Failed to load exchange-rate sync status.",
            "Failed to load exchange-rate sync status.",
          ),
        ),
      );
      setSyncStatus([]);
    } finally {
      setSyncLoading(false);
    }
  };

  const loadAutoSyncStatus = async () => {
    setAutoSyncLoading(true);
    setAutoSyncError("");
    try {
      const data = await getExchangeRateAutoSyncStatus();
      setAutoSyncStatus(data || null);
    } catch (err) {
      setAutoSyncError(
        normalizeErrorMessage(
          err,
          "Failed to load exchange-rate auto-sync status.",
        ),
      );
      setAutoSyncStatus(null);
    } finally {
      setAutoSyncLoading(false);
    }
  };

  const { handleManualSync, handleFullSync } = createExchangeRateSyncHandlers({
    activeTab,
    baseCurrency,
    supportedCurrencies,
    syncing,
    setSyncing,
    setSyncError,
    showToast,
    syncExchangeRates,
    syncExchangeRatesStream,
    setFullSyncProgress,
    loadSyncStatus,
    loadAutoSyncStatus,
    loadOverview,
    loadMatrix,
    loadPairs,
  });

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const handleApplyFilters = async () => {
    setMatrixPage(1);
    setPairsPage(1);
    if (activeTab === "matrix") return loadMatrix({ page: 1 });
    if (activeTab === "pairs") return loadPairs({ page: 1 });
    return Promise.all([loadMatrix({ page: 1 }), loadPairs({ page: 1 })]);
  };
  const handleResetFilters = async () => {
    const defaultFilters = createDefaultFilters();
    setFilters(defaultFilters);
    setMatrixPage(1);
    setPairsPage(1);
    await Promise.all([
      loadMatrix({ filters: defaultFilters, page: 1 }),
      loadPairs({ filters: defaultFilters, page: 1 }),
    ]);
  };

  const handleMatrixPageChange = async (page) => {
    const nextPage = Math.max(1, Number(page || 1));
    setMatrixPage(nextPage);
    await loadMatrix({ page: nextPage });
  };

  const handleMatrixPageSizeChange = async (pageSize) => {
    const nextPageSize = Number(pageSize || 12);
    setMatrixPage(1);
    setMatrixPageSize(nextPageSize);
    await loadMatrix({ page: 1, pageSize: nextPageSize });
  };

  const handleMatrixSortChange = async (key) => {
    const sort = nextSort(matrixSort, key);
    setMatrixSort(sort);
    setMatrixPage(1);
    await loadMatrix({ page: 1, sort });
  };

  const handlePairsPageChange = async (page) => {
    const nextPage = Math.max(1, Number(page || 1));
    setPairsPage(nextPage);
    await loadPairs({ page: nextPage });
  };

  const handlePairsPageSizeChange = async (pageSize) => {
    const nextPageSize = Number(pageSize || 50);
    setPairsPage(1);
    setPairsPageSize(nextPageSize);
    await loadPairs({ page: 1, pageSize: nextPageSize });
  };

  const handlePairsSortChange = async (key) => {
    const sort = nextSort(pairsSort, key);
    setPairsSort(sort);
    setPairsPage(1);
    await loadPairs({ page: 1, sort });
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadOverview();
      loadSyncStatus();
      loadAutoSyncStatus();
      if (user?.default_currency)
        setBaseCurrency(String(user.default_currency).toUpperCase());
    }
  }, [user?.is_admin, user?.default_currency]);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (activeTab === "matrix" && !matrix && !matrixLoading) loadMatrix();
    if (activeTab === "pairs" && !pairs && !pairsLoading) loadPairs();
  }, [activeTab, user?.is_admin]);

  return {
    supportedCurrencies,
    activeTab,
    setActiveTab,
    overview,
    matrix,
    pairs,
    sortedSyncStatus,
    overviewLoading,
    matrixLoading,
    pairsLoading,
    syncLoading,
    autoSyncLoading,
    overviewError,
    matrixError,
    pairsError,
    syncError,
    autoSyncError,
    syncing,
    fullSyncProgress,
    baseCurrency,
    setBaseCurrency,
    selectedCell,
    autoSyncStatus,
    setSelectedCell,
    filters,
    matrixPage,
    matrixPageSize,
    matrixSort,
    pairsPage,
    pairsPageSize,
    pairsSort,
    availableBaseCurrencies,
    availableQuoteCurrencies,
    loadOverview,
    loadMatrix,
    loadPairs,
    loadSyncStatus,
    loadAutoSyncStatus,
    handleManualSync,
    handleFullSync,
    handleFilterChange,
    handleApplyFilters,
    handleResetFilters,
    handleMatrixPageChange,
    handleMatrixPageSizeChange,
    handleMatrixSortChange,
    handlePairsPageChange,
    handlePairsPageSizeChange,
    handlePairsSortChange,
  };
}
